import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, ArrowRight, Navigation, Clock, Euro } from 'lucide-react';
import { getNearbyCommunes } from '../utils/communeProximity';

/**
 * Cluster géographique : affiche les communes voisines d'une commune donnée,
 * triées par distance GPS réelle, avec tarif Goloire-Cab calculé.
 *
 * Renforce le maillage interne SEO et permet à l'utilisateur d'explorer
 * naturellement les communes proches.
 */
const NearbyCommunes = ({ currentSlug, currentCity, limit = 6 }) => {
  const nearby = useMemo(() => getNearbyCommunes(currentSlug, limit), [currentSlug, limit]);

  if (!nearby.length) return null;

  return (
    <section
      className="py-12 bg-gradient-to-br from-gray-50 to-goloire-navy-50"
      data-testid="nearby-communes-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-goloire-gold/10 rounded-full text-goloire-gold font-medium mb-4">
            <Navigation className="w-5 h-5" aria-hidden="true" />
            <span>VTC à proximité</span>
          </div>
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
            Communes VTC proches de {currentCity}
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Notre service de chauffeur privé Goloire-Cab couvre toute la région.
            Voici les {nearby.length} communes les plus proches de {currentCity}, avec leurs tarifs vers Nantes.
          </p>
        </div>

        {/* Grille des communes voisines */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {nearby.map((c, index) => (
            <Link
              key={c.slug}
              to={`/${c.slug}`}
              data-testid={`nearby-commune-${c.slug}`}
              className="group block p-5 bg-white rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 border border-gray-100 hover:border-goloire-gold relative overflow-hidden"
            >
              {index < 3 && (
                <div className="absolute top-2 right-2 px-2 py-1 bg-goloire-gold text-white text-xs font-bold rounded-full">
                  #{index + 1} proche
                </div>
              )}

              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-goloire-navy-50 rounded-lg flex items-center justify-center group-hover:bg-goloire-gold/20 transition-colors">
                  <MapPin className="w-5 h-5 text-goloire-gold" aria-hidden="true" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-gray-900 group-hover:text-goloire-navy transition-colors">
                    VTC {c.city}
                  </h3>

                  {/* Distance, temps, tarif */}
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2 text-sm">
                    <span className="flex items-center gap-1 text-gray-500">
                      <Navigation className="w-3 h-3" aria-hidden="true" />
                      {c.distance.toFixed(0)} km
                    </span>
                    <span className="flex items-center gap-1 text-gray-500">
                      <Clock className="w-3 h-3" aria-hidden="true" />
                      ~{c.durationMin} min
                    </span>
                    <span className="flex items-center gap-1 font-medium text-goloire-gold">
                      <Euro className="w-3 h-3" aria-hidden="true" />
                      dès {c.price}€
                    </span>
                  </div>

                  <p className="text-xs text-gray-400 mt-2">
                    Tarif fixe vers Nantes
                  </p>
                </div>
              </div>

              <div className="absolute bottom-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity">
                <ArrowRight className="w-5 h-5 text-goloire-gold" aria-hidden="true" />
              </div>
            </Link>
          ))}
        </div>

        {/* Trajets populaires depuis la commune */}
        <div className="mt-8 p-6 bg-white rounded-xl border border-gray-200">
          <h3 className="font-semibold text-goloire-navy mb-4 flex items-center gap-2">
            <MapPin className="w-5 h-5 text-goloire-gold" aria-hidden="true" />
            Trajets populaires depuis {currentCity}
          </h3>
          <div className="flex flex-wrap gap-2">
            {[
              { to: '/hub-aeroport-nantes', label: `${currentCity} → Aéroport Nantes` },
              { to: '/hub-gare-nantes', label: `${currentCity} → Gare SNCF Nantes` },
              { to: '/vtc-la-baule', label: `${currentCity} → La Baule` },
              { to: '/vtc-saint-nazaire', label: `${currentCity} → Saint-Nazaire` },
              { to: '/hub-puy-du-fou', label: `${currentCity} → Puy du Fou` },
            ].map((route, idx) => (
              <Link
                key={`${route.label}-${idx}`}
                to={route.to}
                data-testid={`popular-route-${idx}`}
                className="px-4 py-2 bg-goloire-navy-50 hover:bg-goloire-gold/20 text-gray-700 hover:text-goloire-navy rounded-lg text-sm transition-colors"
              >
                {route.label}
              </Link>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="text-center mt-8">
          <Link
            to="/communes"
            data-testid="all-communes-cta"
            className="inline-flex items-center px-8 py-4 bg-goloire-navy text-white rounded-xl hover:bg-goloire-navy-light transition-colors font-medium shadow-lg hover:shadow-xl"
          >
            <MapPin className="w-5 h-5 mr-2" aria-hidden="true" />
            Voir les 300+ communes desservies
            <ArrowRight className="w-5 h-5 ml-2" aria-hidden="true" />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default NearbyCommunes;
